import { Box, Button, IconButton, type SxProps, type Theme } from '@mui/material';
import { forwardRef } from 'react';
import {
  type ActionProps,
  type DragHandleProps,
  type FieldSelectorProps,
  TestID,
  toFlatOptionArray,
  type ValueSelectorProps,
} from 'react-querybuilder';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { SelectMenu } from 'src/components/SelectMenu';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import {
  type ConditionFieldOption,
  getAttributeTitles,
  RULE_ATTRIBUTE_FIELD_CLASS,
  toAttributeMenuOptions,
} from './condition-fields';

const PLACEHOLDER_BY_TEST_ID: Partial<Record<string, string>> = {
  [TestID.operators]: 'Operator',
};

const TEST_ID_BY_SELECTOR: Partial<Record<string, string>> = {
  [TestID.operators]: 'condition-operator',
  [TestID.combinators]: 'condition-combinator',
};

const removeButtonSx: SxProps<Theme> = {
  padding: 0.5,
  borderRadius: (theme) => theme.shape.borderRadius / 8,
};

/**
 * The rule's `field` holds the whole field object once picked (the save mappers read its
 * `conditionType` and `uiType` from there), and a plain name before that.
 */
function getPickedFieldKey(value: unknown): string | null {
  if (value && typeof value === 'object' && 'value' in value) {
    return String((value as ConditionFieldOption).value);
  }
  return typeof value === 'string' && value !== '' && value !== '~' ? value : null;
}

export const ConditionFieldSelector = (props: FieldSelectorProps) => {
  // not `toFlatOptionArray`: it reads a field that carries its own `options` as an option group
  const options = props.options as unknown as ConditionFieldOption[];
  const pickedKey = getPickedFieldKey(props.value ?? props.rule?.field);
  const picked = options.find((option) => String(option.value) === pickedKey);
  const titles = getAttributeTitles(options);

  return (
    <Box
      className={[props.className, RULE_ATTRIBUTE_FIELD_CLASS].filter(Boolean).join(' ')}
      data-testid="condition-attribute"
      sx={{ minWidth: 0 }}
    >
      <SelectMenu
        label="Attribute"
        variant="field"
        size="small"
        sectioned
        search
        options={toAttributeMenuOptions(options)}
        value={pickedKey}
        placeholder="Attribute"
        triggerLabel={pickedKey ? titles.get(pickedKey) : undefined}
        tooltip={picked?.label}
        disabled={props.disabled}
        onChange={(key) => {
          const option = options.find((item) => String(item.value) === key);
          if (option) {
            props.handleOnChange(option);
          }
        }}
      />
    </Box>
  );
};

/** The operator and AND / OR pickers: short lists, so the menu fits its rows. */
export const ConditionValueSelector = (props: ValueSelectorProps) => {
  const options = toFlatOptionArray(props.options);

  return (
    <Box
      className={props.className}
      title={props.title}
      data-testid={props.testID ? TEST_ID_BY_SELECTOR[props.testID] : undefined}
      sx={{ minWidth: 0 }}
    >
      <SelectMenu
        label={props.title ?? 'Value'}
        variant="field"
        size="small"
        options={options.map((option) => ({ value: option.name, title: option.label }))}
        value={typeof props.value === 'string' && props.value !== '' ? props.value : null}
        placeholder={props.testID ? PLACEHOLDER_BY_TEST_ID[props.testID] : undefined}
        menuWidth="max-content"
        disabled={props.disabled}
        onChange={(key) => props.handleOnChange(key)}
      />
    </Box>
  );
};

// react-querybuilder's drag-and-drop attaches its drag source through this ref
export const ConditionDragHandle = forwardRef<HTMLSpanElement, DragHandleProps>(
  ({ className, title }, ref) => (
    <span ref={ref} className={className} title={title} aria-hidden>
      <DynamicIcon name="grip-vertical" library="lucide" />
    </span>
  ),
);
ConditionDragHandle.displayName = 'ConditionDragHandle';

interface ConditionActionButtonProps extends ActionProps {
  label: string;
  testId: string;
}

// an empty title keeps react-querybuilder's own tooltip off the button
const ConditionActionButton = ({
  className,
  disabled,
  handleOnClick,
  label,
  testId,
}: ConditionActionButtonProps) => (
  <Button
    className={className}
    disabled={disabled}
    onClick={handleOnClick}
    title=""
    data-testid={testId}
  >
    {label}
  </Button>
);

const ConditionRemoveButton = ({
  className,
  disabled,
  handleOnClick,
  label,
  testId,
}: ConditionActionButtonProps) => (
  <Tooltip title={label}>
    <IconButton
      className={className}
      disabled={disabled}
      onClick={handleOnClick}
      aria-label={label}
      data-testid={testId}
      sx={removeButtonSx}
    >
      <DynamicIcon name="trash-2" library="lucide" />
    </IconButton>
  </Tooltip>
);

export const AddConditionAction = (props: ActionProps) => (
  <ConditionActionButton {...props} label="+ Condition" testId="condition-add" />
);

export const AddGroupAction = (props: ActionProps) => (
  <ConditionActionButton {...props} label="+ Group" testId="condition-add-group" />
);

export const RemoveConditionAction = (props: ActionProps) => (
  <ConditionRemoveButton {...props} label="Remove this condition" testId="condition-remove" />
);

export const RemoveGroupAction = (props: ActionProps) => (
  <ConditionRemoveButton
    {...props}
    label="Remove this group"
    testId="condition-group-remove"
  />
);
